import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { Warning, Pause } from '@mui/icons-material'
import { isOverdue, isPaused } from '../utils/dateUtils'
import { compactTooltipConfig } from '../utils/tooltipStyles'
import { useTheme } from '../App'
import { getIndicatorColor } from '../utils/statusColors'

interface StatusIndicatorsProps {
  endDate: string | null
  status: string | { name: string } | null
  context?: 'table' | 'timeline'
  showOverdue?: boolean
  showPaused?: boolean
}

/**
 * Reusable component for displaying status indicators (warning, pause, etc.)
 * Provides consistent styling and behavior across different views
 */
const StatusIndicators: React.FC<StatusIndicatorsProps> = ({
  endDate,
  status,
  context = 'table',
  showOverdue = true,
  showPaused = true
}) => {
  const { darkMode } = useTheme()

  // Normalize status to string
  const statusString = typeof status === 'string' ? status : status?.name || ''

  // Check conditions
  const shouldShowOverdue = showOverdue && isOverdue(endDate, statusString)
  const shouldShowPaused = showPaused && endDate && isPaused(statusString)

  // Don't render anything if no indicators should show
  if (!shouldShowOverdue && !shouldShowPaused) {
    return null
  }

  // Common styling for indicators
  const getIndicatorStyles = (type: 'warning' | 'pause') => {
    const baseStyles = {
      fontSize: 16,
      cursor: 'help',
      zIndex: 1
    }

    if (type === 'warning') {
      return {
        ...baseStyles,
        color: getIndicatorColor('warning', darkMode),
        animation: 'pulse 2s infinite'
      }
    }

    // Pause indicator styles
    return {
      ...baseStyles,
      color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)',
      backgroundColor: darkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
      borderRadius: '50%',
      padding: '2px',
      boxShadow: darkMode
        ? '0 1px 3px rgba(0,0,0,0.8)'
        : '0 1px 3px rgba(0,0,0,0.3)'
    }
  }

  const formatEndDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Overdue Warning Indicator */}
      {shouldShowOverdue && (
        <Tooltip
          title={`Overdue: End date was ${endDate ? formatEndDate(endDate) : 'unknown'}`}
          {...compactTooltipConfig}
        >
          <Warning sx={getIndicatorStyles('warning')} />
        </Tooltip>
      )}

      {/* Paused Status Indicator */}
      {shouldShowPaused && (
        <Tooltip
          title="Timeline may be outdated - initiative paused"
          {...compactTooltipConfig}
        >
          <Pause sx={getIndicatorStyles('pause')} />
        </Tooltip>
      )}
    </Box>
  )
}

export default StatusIndicators
